import moment from 'moment';
import { Icon } from '@sb1/ffe-icons-react';
import candleIcon from '@sb1/ffe-icons/icons/open/300/sm/candle.svg';
import favoriteIcon from '@sb1/ffe-icons/icons/open/300/sm/favorite.svg';
import stylusNoteIcon from '@sb1/ffe-icons/icons/open/300/sm/stylus_note.svg';
import volunteerIcon from '@sb1/ffe-icons/icons/open/500/sm/volunteer_activism.svg';
import Image from '../../components/image/Image';
import { useI18nTexts } from '../i18n/useI18nTexts';
import {
    ANONYMOUS_TEXT_KEY,
    ACTIVITY_LIT_CANDLE_TEXT_KEY,
    ACTIVITY_SUPPORTED_TEXT_KEY,
    ACTIVITY_SUPPORTED_AND_WROTE_MEMORIAL_TEXT_KEY,
    ACTIVITY_SUPPORTED_AND_WROTE_GREETING_TEXT_KEY,
    ACTIVITY_SUPPORTED_AND_LIT_CANDLE_TEXT_KEY,
    ACTIVITY_WROTE_MEMORIAL_TEXT_KEY,
} from '../i18n/project-i18n-model';
import { useProject } from '../project-hooks/useProject';
import { useProjectLocale } from '../i18n/useProjectLocale';
import { isMinnespleis, USER_COMMENT_TYPES } from '../project-model';
import styles from './activity-card.module.less';
import Avatar from '../../components/avatar/Avatar';
import { MicroText, SmallText, StrongText } from '@sb1/ffe-core-react';
import { CANDLE_REACTION } from '../project-actions';

const getActionIcon = ({ hasCommentOrImage, hasCandle }) => {
    if (hasCandle) return candleIcon;
    if (hasCommentOrImage) return stylusNoteIcon;
    return favoriteIcon;
};

const getActionText = ({ isCommentWithoutTransaction, hasCandle, hasCommentOrImage, isMinnespleis, texts }) => {
    if (isCommentWithoutTransaction) {
        return hasCandle ? texts[ACTIVITY_LIT_CANDLE_TEXT_KEY] : texts[ACTIVITY_WROTE_MEMORIAL_TEXT_KEY];
    }
    if (hasCandle) return texts[ACTIVITY_SUPPORTED_AND_LIT_CANDLE_TEXT_KEY];
    if (hasCommentOrImage)
        return isMinnespleis
            ? texts[ACTIVITY_SUPPORTED_AND_WROTE_MEMORIAL_TEXT_KEY]
            : texts[ACTIVITY_SUPPORTED_AND_WROTE_GREETING_TEXT_KEY];
    return texts[ACTIVITY_SUPPORTED_TEXT_KEY];
};

const ActivityCard = ({ transaction, comment, anonymousActionText, showAvatar, children }) => {
    const project = useProject();
    const locale = useProjectLocale();
    const texts = useI18nTexts([
        ANONYMOUS_TEXT_KEY,
        ACTIVITY_LIT_CANDLE_TEXT_KEY,
        ACTIVITY_SUPPORTED_TEXT_KEY,
        ACTIVITY_SUPPORTED_AND_WROTE_MEMORIAL_TEXT_KEY,
        ACTIVITY_SUPPORTED_AND_WROTE_GREETING_TEXT_KEY,
        ACTIVITY_SUPPORTED_AND_LIT_CANDLE_TEXT_KEY,
        ACTIVITY_WROTE_MEMORIAL_TEXT_KEY,
    ]);

    const isAnonymous = transaction.anonymous;
    const name = isAnonymous ? texts[ANONYMOUS_TEXT_KEY] : transaction.name || texts[ANONYMOUS_TEXT_KEY];

    const isCommentWithoutTransaction =
        transaction.itemType === USER_COMMENT_TYPES.REACTION ||
        transaction.itemType === USER_COMMENT_TYPES.COMMENT_WITHOUT_TRANSACTION;
    const hasCandle = comment?.id && comment?.reaction === CANDLE_REACTION && !comment?.hidden;
    const hasCommentOrImage = comment?.id && (!!comment?.comment || !!comment?.img_url) && !comment?.hidden;

    const actionIcon = getActionIcon({ hasCommentOrImage, hasCandle });
    const actionText =
        anonymousActionText ??
        getActionText({
            isCommentWithoutTransaction,
            hasCandle,
            hasCommentOrImage,
            isMinnespleis: isMinnespleis(project),
            texts,
        });

    const createdAt = moment(transaction.createdAt);
    const timeDisplay = createdAt.isBefore(moment().subtract(7, 'days'))
        ? createdAt.locale(locale).format('ll')
        : createdAt.locale(locale).fromNow();

    if (!anonymousActionText && (!comment || comment.hidden)) {
        return null;
    }

    return (
        <div className={styles.activityCard}>
            {showAvatar && (
                <Avatar
                    iconFileUrl={volunteerIcon}
                    initialsSource={transaction?.name}
                    className={styles.activityAvatar}
                />
            )}
            <div className={styles.cardContent}>
                <div className={styles.nameAndTime}>
                    <StrongText>{name}</StrongText>
                    <MicroText className={styles.time}>
                        <time dateTime={createdAt.format()} suppressHydrationWarning>
                            {timeDisplay}
                        </time>
                    </MicroText>
                </div>
                <SmallText className={styles.action}>
                    <Icon fileUrl={actionIcon} size="sm" />
                    <span>{actionText}</span>
                </SmallText>
                {hasCommentOrImage && (
                    <>
                        {comment.comment && <div>{comment.comment}</div>}
                        {comment.img_url && (
                            <div>
                                <Image className={styles.commentImage} src={comment.img_url} />
                            </div>
                        )}
                    </>
                )}
                {children}
            </div>
        </div>
    );
};

export default ActivityCard;
